import { IonIcon } from "@ionic/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { chevronForwardOutline, documentsOutline } from "ionicons/icons";
import { InfoModal } from "@/components/molecules/info-modal";
import { DeclarationPreviewModal } from "@/components/molecules/shift/declaration-preview-modal";
import { DetailCard } from "@/components/molecules/shift/detail-card";
import { EntryStatusChip } from "@/components/molecules/shift/entry-status-chip";
import { HoursFormModal } from "@/components/molecules/shift/hours-form-modal";
import { HoursPreviewModal } from "@/components/molecules/shift/hours-preview-modal";
import { TravelExpensesFormModal } from "@/components/molecules/shift/travel-expenses-form-modal";
import { type Shift } from "@/lib/backend/api";
import {
  useDeclarationInfoSections,
  useHoursInfoSections,
  useTravelExpensesInfoSections,
} from "@/lib/constants/application-info";
import { useCompletedShiftActions } from "@/lib/hooks/use-completed-shift-actions";
import { useShiftDisplayTimeDetailed } from "@/lib/hooks/use-shift-display-time";
import { timeIcon } from "@/lib/icons/time.icon";
import { Routes } from "@/lib/router";
import { opensHoursPreview } from "@/lib/shift/employee-hours-response";
import { isSettled, needsAttention, toEntryState } from "@/lib/shift/entry-status";

/**
 * The settlement half of a finished shift: your hours and your expense claim.
 *
 * A shift screen for something already worked cannot be the same screen as one for something coming
 * up — offering it to a colleague or asking to be taken off it are meaningless once the night is
 * over. This is what stands in their place: the two things that are still moving, and where they
 * stand.
 *
 * It owns the same modals as the compact row on Afgerond, driven by the same
 * `useCompletedShiftActions`. The two render differently on purpose — a row has space for a chip, a
 * screen has space for the numbers — but neither has its own idea of what the buttons do.
 */
export function ShiftSettlement({ shift }: { shift: Shift }) {
  const { t } = useTranslation();
  const declarationInfoSections = useDeclarationInfoSections();
  const hoursInfoSections = useHoursInfoSections();
  const travelExpensesInfoSections = useTravelExpensesInfoSections();
  const infoModalRef = useRef<HTMLIonModalElement>(null);
  const hoursInfoModalRef = useRef<HTMLIonModalElement>(null);
  const hoursPreviewModalRef = useRef<HTMLIonModalElement>(null);
  const travelExpensesInfoModalRef = useRef<HTMLIonModalElement>(null);

  const displayTime = useShiftDisplayTimeDetailed(shift);

  const {
    permissions,
    hourButtonState,
    declarationButtonState,
    expenseClaim,
    expenseClaimFileUrl,
    isLoadingExpenseClaim,
    isErrorExpenseClaim,
    formHours,
    FormElementHours,
    employeeResponseMutation,
    formTravelExpenses,
    FormElementExpenses,
    validator,
    ClockInWebScanner,
    ClockOutWebScanner,
    openHoursModal,
    openExpensesModal,
    openDeclarationPreview,
    closeModal,
    modalHoursRef,
    modalExpensesRef,
    modalDeclarationPreviewRef,
  } = useCompletedShiftActions(shift);

  const hoursState = toEntryState(shift.shiftTimeEntry?.status);
  const expensesState = toEntryState(expenseClaim?.status);
  // Settled hours are settled whatever the fields look like: an approved or paid entry with no
  // break time reads as incomplete, and would otherwise be handed a button to submit hours that
  // are already on a payslip.
  const hoursNeedYou =
    !isSettled(hoursState) && (!displayTime.isFullyDeclared || needsAttention(hoursState));

  const handleHoursClick = () => {
    if (opensHoursPreview(shift.shiftTimeEntry, displayTime.isLocked)) {
      // Read-only zodra de uren vastliggen, en ook zodra het kantoor ze invoerde: dan corrigeert
      // de medewerker ze niet maar reageert hij erop, en die reactie zit hier. Eigen invoer blijft
      // tot de goedkeuring bewerkbaar; ingediend hebben is geen reden om er niet meer bij te
      // mogen (#495).
      hoursPreviewModalRef.current?.present();
    } else if (hourButtonState.action) {
      hourButtonState.action();
    } else {
      openHoursModal();
    }
  };

  // Arriving with `?declare=1` means the employee already said yes to the question raised when
  // their hours went in. Opening the form for them honours that answer instead of asking twice.
  // Waits for the claim query, or a shift that already has one would get the form anyway.
  const search = Routes.ShiftDetail.useSearchParams();
  const hasAutoOpened = useRef(false);

  useEffect(() => {
    if (hasAutoOpened.current || !search.declare || isLoadingExpenseClaim) {
      return;
    }
    if (expenseClaim || !declarationButtonState.isVisible) {
      return;
    }

    hasAutoOpened.current = true;
    formTravelExpenses.reset();
    openExpensesModal();
  }, [
    search.declare,
    isLoadingExpenseClaim,
    expenseClaim,
    declarationButtonState.isVisible,
    formTravelExpenses,
    openExpensesModal,
  ]);

  const handleDeclarationClick = () => {
    if (expenseClaim) {
      openDeclarationPreview();
    } else {
      // Same Android reset-before-open as the row: a first upload can fail on stale form state.
      formTravelExpenses.reset();
      openExpensesModal();
    }
  };

  return (
    <>
      <DetailCard title={t("routes:shift-detail.settlement")}>
        {hourButtonState.isVisible && (
          <SettlementRow
            icon={timeIcon}
            label={t("shift:hours.title")}
            detail={
              displayTime.isFullyDeclared
                ? `${displayTime.startTime} - ${displayTime.endTime}${
                    displayTime.durationDutch ? ` · ${displayTime.durationDutch}` : ""
                  }`
                : t("routes:shift-detail.hours-missing")
            }
            status={shift.shiftTimeEntry?.status}
            kind="hours"
            // The label says what is actually being asked. After a rejection that is correcting
            // something, not entering it for the first time.
            //
            // Mag de medewerker zelf niets invoeren, dan staat de regel er om te lezen en op te
            // reageren (admin#1093). Een actieknop hoort daar niet bij: die belooft een formulier
            // dat niet opengaat.
            actionLabel={
              hoursNeedYou && !hourButtonState.isReadOnly
                ? permissions.enableQRCodeClocking
                  ? hourButtonState.text
                  : needsAttention(hoursState)
                    ? t("shift:hours.edit")
                    : t("shift:hours.title")
                : undefined
            }
            onClick={handleHoursClick}
          />
        )}

        {declarationButtonState.isVisible && (
          <SettlementRow
            icon={documentsOutline}
            label={t("shift:declaration.title")}
            detail={
              expenseClaim
                ? t("routes:shift-detail.declaration-submitted")
                : t("routes:shift-detail.declaration-none")
            }
            status={expenseClaim?.status}
            kind="expenses"
            actionLabel={expenseClaim ? undefined : t("shift:declaration.button.submit")}
            onClick={handleDeclarationClick}
          />
        )}
      </DetailCard>

      <HoursFormModal
        modalRef={modalHoursRef}
        hoursInfoModalRef={hoursInfoModalRef}
        formHours={formHours}
        FormElementHours={FormElementHours}
        validator={validator}
        closeModal={closeModal}
      />
      <TravelExpensesFormModal
        modalRef={modalExpensesRef}
        travelExpensesInfoModalRef={travelExpensesInfoModalRef}
        formTravelExpenses={formTravelExpenses}
        FormElementExpenses={FormElementExpenses}
        closeModal={() => {
          formTravelExpenses.reset();
          closeModal();
        }}
      />
      <DeclarationPreviewModal
        modalRef={modalDeclarationPreviewRef}
        infoModalRef={infoModalRef}
        expenseClaim={expenseClaim}
        expenseClaimFileUrl={expenseClaimFileUrl}
        isLoadingExpenseClaim={isLoadingExpenseClaim}
        isErrorExpenseClaim={isErrorExpenseClaim}
        closeModal={closeModal}
      />
      {/* Klopt / klopt niet (admin#624) is wired here as well as on the legacy row: the reworked
          screen replaces that row, so leaving it out would quietly drop a feature for exactly the
          organizations testing the new app. Same mutation, same hook. */}
      <HoursPreviewModal
        modalRef={hoursPreviewModalRef}
        hoursInfoModalRef={hoursInfoModalRef}
        shift={shift}
        durationDutch={displayTime.durationDutch}
        onRespond={(response, note) => employeeResponseMutation.mutate({ response, note })}
        isRespondingToHours={employeeResponseMutation.isPending}
      />
      <InfoModal
        modalRef={infoModalRef}
        title={t("shift:declaration.info.title")}
        sections={declarationInfoSections}
      />
      <InfoModal
        modalRef={hoursInfoModalRef}
        title={t("shift:hours.info.title")}
        sections={hoursInfoSections}
      />
      <InfoModal
        modalRef={travelExpensesInfoModalRef}
        title="Informatie over reiskosten"
        sections={travelExpensesInfoSections}
      />
      {ClockInWebScanner && <ClockInWebScanner />}
      {ClockOutWebScanner && <ClockOutWebScanner />}
    </>
  );
}

function SettlementRow({
  icon,
  label,
  detail,
  status,
  kind,
  actionLabel,
  onClick,
}: {
  icon: string;
  label: string;
  detail: string;
  status: string | null | undefined;
  kind: "hours" | "expenses";
  /** Present only while something is expected of the employee; otherwise the row just opens. */
  actionLabel?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-4 py-4 text-left transition-opacity active:opacity-70"
    >
      <IonIcon icon={icon} className="size-5 shrink-0 text-txt-tertiary" aria-hidden="true" />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-md font-medium leading-[1.5] text-txt-primary">{label}</span>
          <EntryStatusChip kind={kind} status={status} />
        </div>
        <span className="text-sm leading-[1.4] text-txt-secondary">{detail}</span>
      </div>

      {actionLabel ? (
        <span className="shrink-0 rounded-lg border-2 border-solid border-button-primary bg-button-primary px-3 py-1.5 text-13 font-semibold leading-[1.4] text-txt-inverted">
          {actionLabel}
        </span>
      ) : (
        <IonIcon
          icon={chevronForwardOutline}
          className="size-4 shrink-0 text-txt-tertiary"
          aria-hidden="true"
        />
      )}
    </button>
  );
}
